/**
 * P2d — Admin Audit Log page with filters.
 *
 * Reads /api/audit with server-side filters:
 *   - action (text-ilike)
 *   - entity_type (dropdown from /api/audit/facets)
 *   - actor_user_id
 *   - entity_id
 *   - from_date / to_date (ISO)
 *
 * Renders one row per entry with expandable before/after JSON.
 */
import React, { useEffect, useState } from "react";
import { Section, Card, Badge, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";


function fmtDateTime(iso) { return iso ? new Date(iso).toLocaleString("it-IT") : "—"; }


export default function AdminAuditLogPage() {
  const { apiGet } = useAuth();
  const [rows, setRows] = useState(null);
  const [facets, setFacets] = useState({ actions: [], entity_types: [] });
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({
    action: "", entity_type: "", actor_user_id: "",
    entity_id: "", from_date: "", to_date: "",
    limit: 200,
  });
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const qp = new URLSearchParams();
      for (const k of ["action", "entity_type", "actor_user_id", "entity_id", "from_date", "to_date"]) {
        if (f[k]) qp.set(k, k.endsWith("_date") ? new Date(f[k]).toISOString() : f[k]);
      }
      qp.set("limit", String(f.limit || 200));
      const data = await apiGet(`/api/audit?${qp.toString()}`);
      setRows(data);
    } catch (e) { setErr(e?.message || String(e)); setRows([]); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [fac, us] = await Promise.all([
          apiGet("/api/audit/facets"),
          apiGet("/api/users").catch(() => []),
        ]);
        setFacets(fac); setUsers(us);
        await load();
      } catch (e) { setErr(e?.message || String(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const userById = Object.fromEntries((users || []).map(u => [u.id, u]));

  return (
    <Section title="Audit Log" subtitle="Registro delle azioni server — append-only, filtrabile"
             data-testid="audit-log-page">
      <Card>
        <Card.Header>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 8, alignItems: "flex-end" }}>
            <div>
              <Label>Azione (contiene)</Label>
              <Input value={f.action} placeholder="es. contract"
                     onChange={(e) => setF({ ...f, action: e.target.value })}
                     data-testid="audit-filter-action" />
            </div>
            <div>
              <Label>Entità</Label>
              <select className="ds-input" value={f.entity_type}
                      onChange={(e) => setF({ ...f, entity_type: e.target.value })}
                      data-testid="audit-filter-entity">
                <option value="">— Tutte —</option>
                {facets.entity_types.map(et => <option key={et} value={et}>{et}</option>)}
              </select>
            </div>
            <div>
              <Label>Utente</Label>
              <select className="ds-input" value={f.actor_user_id}
                      onChange={(e) => setF({ ...f, actor_user_id: e.target.value })}
                      data-testid="audit-filter-user">
                <option value="">— Tutti —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
              </select>
            </div>
            <div>
              <Label>Dal</Label>
              <Input type="datetime-local" value={f.from_date}
                     onChange={(e) => setF({ ...f, from_date: e.target.value })}
                     data-testid="audit-filter-from" />
            </div>
            <div>
              <Label>Al</Label>
              <Input type="datetime-local" value={f.to_date}
                     onChange={(e) => setF({ ...f, to_date: e.target.value })}
                     data-testid="audit-filter-to" />
            </div>
            <div>
              <Label>Limite</Label>
              <select className="ds-input" value={f.limit}
                      onChange={(e) => setF({ ...f, limit: Number(e.target.value) })}>
                <option value={50}>50</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
            <Button variant="primary" onClick={load} data-testid="audit-apply">Applica</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {err && <Alert variant="danger">Errore: {err}</Alert>}
          {!rows && !err && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessuna voce con i filtri correnti.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="audit-table">
                <thead><tr>
                  <th>Quando</th><th>Chi</th><th>Ruolo</th><th>Azione</th><th>Entità</th><th>Entity ID</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <React.Fragment key={r.id}>
                      <tr data-testid={`audit-row-${r.id}`}>
                        <td style={{ fontSize: 12 }}>{fmtDateTime(r.at)}</td>
                        <td style={{ fontSize: 12 }}>{userById[r.actor_user_id]?.email || r.actor_user_id?.slice(0, 8) || "sistema"}</td>
                        <td><Badge tone="neutral">{r.actor_role_key || "—"}</Badge></td>
                        <td><code style={{ fontSize: 11 }}>{r.action}</code></td>
                        <td>{r.entity_type || "—"}</td>
                        <td><code style={{ fontSize: 10, color: "#64748b" }}>{r.entity_id?.slice(0, 8) || "—"}</code></td>
                        <td>
                          {(r.before || r.after) && (
                            <Button size="sm" onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                                    data-testid={`audit-toggle-${r.id}`}>
                              {expanded === r.id ? "▲" : "▼"}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {expanded === r.id && (
                        <tr>
                          <td colSpan={7} style={{ background: "#F8FAFC" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: 8 }}>
                              <div>
                                <strong>Before</strong>
                                <pre style={{ fontSize: 11, background: "white", padding: 6, borderRadius: 4, maxHeight: 260, overflow: "auto" }}>
                                  {JSON.stringify(r.before || {}, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <strong>After</strong>
                                <pre style={{ fontSize: 11, background: "white", padding: 6, borderRadius: 4, maxHeight: 260, overflow: "auto" }}>
                                  {JSON.stringify(r.after || {}, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Section>
  );
}
